<template>
<div class="page-catalog">
  <section class="catalog-items-wrapper static-container">
    <header class="page-catalog__header">
      <h1 class="page-heading">{{ categoryIsReady ? category.name : 'Каталог товаров' }}</h1>
      <div class="flex-grow"></div>
      <div class="page-catalog__controls">
        <template v-if="!isDesktopLayout">
          <DropdownModal :active.sync="filtersAreActive"
                         placement="bottom-end"
                         :offset="[0, 8]"
                         :overlay="false"
                         :arrow="false"
          >
            <template #toggle>
              <button class="page-catalog__button" aria-label="Фильтры">
                <IconFilters />
              </button>
            </template>
            <template #content>
              <CatalogFilters v-bind="filtersComponentProps.props"
                              v-on="filtersComponentProps.listeners"
                              class="page-catalog__filters page-catalog__filters--mobile"
              >
                <template #before>
                  <FilterWrapper name="Сортировка"
                                 :expanded="mobileExpandedControls.sorting"
                                 @click:toggle="mobileExpandedControls.sorting = !mobileExpandedControls.sorting"
                  >
                    <div v-if="mobileExpandedControls.sorting" class="form-radio-group">
                      <label v-for="(item, name) in availableSorting"
                             :key="name"
                             class="custom-radio"
                      >
                        <span class="custom-radio-label">{{ item.label }}</span>
                        <input type="radio"
                               name="catalog-sorting"
                               :checked="sorting === name"
                               @change="sorting = name"
                        />
                        <span class="radio-button"></span>
                      </label>
                    </div>
                  </FilterWrapper>
                </template>
              </CatalogFilters>
            </template>
          </DropdownModal>
        </template>
        <template v-else>
          <button @click="filtersAreActive = !filtersAreActive"
                  :aria-controls="filtersElId"
                  class="page-catalog__button page-catalog__toggle-filters-button"
          >
            <IconFilters />
            <span>Фильтры</span>
            <i class="fa fa-chevron-down fa-xs" />
          </button>
          <DesktopSortingSelect :available-sortings="availableSorting"
                                v-model="sorting"
                                class="page-catalog__desktop-sorting-select"
          />
        </template>
      </div>
    </header>
    <div v-if="categoryIsReady && category.description"
         v-html="category.description"
         class="prose max-w-full prose-lg mb-4"
    ></div>

    <div v-if="hasChildCategories" class="mb-8">
      <CategoryTiles :categories="category.children" />
    </div>

    <section class="catalog-items-list-container">
      <template v-if="isDesktopLayout">
        <keep-alive>
          <CatalogFilters v-if="filtersAreActive"
                          v-bind="filtersComponentProps.props"
                          v-on="filtersComponentProps.listeners"
                          :id="filtersElId"
                          class="page-catalog__filters page-catalog__filters--desktop"
          />
        </keep-alive>
      </template>
      <div class="catalog-products-list-wrapper">
        <template v-if="hasProducts">
          <products-list v-if="hasProducts"
                         source="catalog_page"
                         :products="products"
                         :category="category"
                         :sidebar-product-id="sidebarProductId"
                         :watch-scroll-end="hasMorePages && !productsAreLoading"
                         @reaching-scroll-end="loadNextPage"
          />
          <!-- I know we have infinite scroll, but need to make sure its accessible and all before we can remove this
          button. Also the infinite scoll functionality is asynchronous, so it may not be loaded yet when the user needs
          to load the next page. -->
          <button v-if="hasMorePages"
                  class="page-catalog__load-more-button"
                  :disabled="productsAreLoading"
                  @click="loadNextPage"
          >
            <i v-if="lastLoadingType === 'append' && productsAreLoading" class="fa fa-spinner fa-spin" />
            <span v-else>Показать ещё</span>
          </button>
        </template>
        <div v-if="!hasProducts && loadedProductsAtLeastOnce" class="empty-products-list">
          <h2 class="empty-products-list-heading">Ничего не найдено</h2>
          <p>По вашему запросу ничего не найдено.</p>
        </div>
        <transition name="opacity">
          <div v-if="displayPreloader" class="page-catalog__preloader-wrapper">
            <preloader class="page-catalog__preloader"/>
          </div>
        </transition>
      </div>
    </section>
  </section>
</div>
</template>

<script>
import { mapGetters } from 'vuex'
import axios from '@/zenky'
import config from '@/config'
import ProductsList from '@/components/products/ProductsList'
import Preloader from '@/components/preloaders/Preloader'
import InjectsSeo from '@/mixins/InjectsSeo'
import cachedState from '@/cachedState'

const stringEmpty = {
  create: () => null,
  isEmpty: v => v === null,
}
/**
 * @type {{ [featureType: string]: { create: () => any, isEmpty: (v: any) => boolean } }}
 */
const emptyFeatureValues = {
  select: stringEmpty,
  radios: stringEmpty,
  range: {
    create: () => ({ from: null, to: null }),
    isEmpty: v => typeof v.from !== 'number' && typeof v.to !== 'number'
  },
  default: {
    create: () => [],
    isEmpty: v => v.length <= 0,
  },
};

const emptyPriceValue = emptyFeatureValues.range;
const priceFilterId = 'price';

export default {
  props: {
    categoryId: {
      required: false,
      type: String,
      default: null,
    },

    sidebarProductId: {
      required: false,
      type: String,
      default: null,
    },
  },

  components: {
    ProductsList,
    Preloader,
    CategoryTiles: () => import('@/components/CategoryTiles'),
    CatalogFilters: () => import('@/components/catalog/CatalogFilters'),
    FilterWrapper: () => import('@/components/catalog/filters/FilterWrapper'),
    DesktopSortingSelect: () => import('@/components/catalog/DesktopSortingSelect'),
    DropdownModal: () => import('@/components/modals/DropdownModal'),
    IconFilters: () => import(/* webpackPreload: true */ '@/assets/images/icons/icon-filters.svg?inline'),
  },

  mixins: [InjectsSeo],

  metaInfo() {
    if (this.categoryIsReady) {
      return this.getMetaInfoFrom(this.category.seo, 'Каталог товаров')
    }

    return {
      title: this.getFullPageTitle('Каталог товаров'),
    }
  },

  created() {
    let initialized = false;
    const unwatch = this.$watch(() => this.readyToLoadProducts, val => {
      if (!val || initialized) {
        return
      }

      initialized = true;

      this.$nextTick(() => unwatch()); // Because we can't call unwatch inside immediate watchers

      // TODO but we can still load features, they don't require activeCityId.

      this.fullReload()
        .then(() => {
          this.watchersState.category = true
          this.watchersState.sorting = true
          this.watchersState.features = true
          this.watchersState.activeCityId = true
        })
        .catch(err => {
          console.error(err)
          this.watchersState.category = true
          this.watchersState.sorting = true
          this.watchersState.features = true
          this.watchersState.activeCityId = true
        })
    }, { immediate: true });
  },

  computed: {
    ...mapGetters({
      categories: 'catalog/flatVisibleCategories',
      settings: 'settings/products',
    }),

    readyToLoadProducts() {
      return !!this.$store.state.store.activeCityId;
    },

    category() {
      return this.categories.find(c => c.short_id === this.categoryId)
    },

    categoryIsReady() {
      return this.category && this.category.id
    },

    hasChildCategories() {
      if (this.settings.display_nested_categories === 'never' || !this.categoryIsReady) {
        return false
      }

      return this.category.children.length > 0
    },

    featuresFilter() {
      const features = {};
      for (const [featureId, val] of Object.entries(this.featureValues)) {
        const feature = this.features.find(f => f.id === featureId);
        if (!this.isFeatureValueEmpty(feature, val)) {
          let filterVal;
          switch (feature.field_type.id) {
            case 'range':
              filterVal = `${val.from}-${val.to}`;
              break;
            default: filterVal = val;
          }
          features[featureId] = filterVal;
        }
      }
      return features;
    },

    productsRequestQuery() {
      let query = {
        page: this.currentPage,
        // Using this weird notation to show that we want the last row to always be filled (except, of course, when this
        // is the last page).
        count: 1 * ((2 * 2) * 3),
        with: config.includes.productsList,
        features: this.featuresFilter,
        city_id: this.$store.state.store.activeCityId,
      }

      if (this.category && this.category.id) {
        query.category_id = this.category.id
      } else if (this.categoryId) {
        query.category_id = this.categoryId
      }

      if (this.sorting && this.sorting !== 'default') {
        query.order_by = this.currentSorting.order_by
      }

      if (!emptyPriceValue.isEmpty(this.priceRange)) {
        query.min_price = this.priceRange.from;
        query.max_price = this.priceRange.to;
      }

      return query
    },

    hasProducts() {
      return this.products.length > 0
    },

    hasMorePages() {
      return this.pagination.total_pages > this.pagination.current_page
    },

    productsAreLoading() {
      return !!this.lastProductsReqest;
    },

    featuresAreLoading() {
      return !!this.lastFeaturesRequest;
    },

    displayPreloader() {
      return !this.readyToLoadProducts
        || this.forcePreloader
        || (this.productsAreLoading && this.lastLoadingType === 'reset')
        || this.featuresAreLoading
    },

    // totalProductsCountLabel() {
    //     let count = this.pagination && this.pagination.total ? this.pagination.total : 0

    //     return pluralize(count, [
    //         `Найден ${count} товар`,
    //         `Найдено ${count} товара`,
    //         `Найдено ${count} товаров`
    //     ], false)
    // },

    hasFeatureFilters() {
      return this.features && this.features.length > 0
    },

    currentSorting() {
      return this.availableSorting[this.sorting]
    },

    isDesktopLayout() {
      return this.$store.getters['ui/isBreakpointActive']('lg'); // WET. See `@layout-breakpoint`.
    },

    feauresWithPrice() {
      return [
        {
          id: priceFilterId,
          name: 'Цена, руб',
          field_type: {
            id: 'range',
          },
          range_type: {
            id: 'float',
          },
          values: [
            { range_value: (this.availablePriceRange?.min?.value || 0) / 100 },
            { range_value: (this.availablePriceRange?.max?.value || 99999900) / 100 },
          ],
        },
        ...this.features,
      ]
    },

    featureValuesWithPrice() {
      return {
        [priceFilterId]: this.priceRange,
        ...this.featureValues
      };
    },

    filtersComponentProps() {
      return {
        props: {
          features: this.feauresWithPrice,
          value: this.featureValuesWithPrice,
          emptyValues: this.emptyFeatureValues,
        },
        listeners: {
          'click:reset': () => {
            this.resetProductsQuery();
            this.loadProducts('reset');
          },
          'click:apply': () => {
            this.loadProducts('reset');
            if (!this.isDesktopLayout) {
              this.filtersAreActive = false;
            }
          },
          'update': (featureId, newVal) => {
            if (featureId === 'price') {
              this.priceRange = newVal;
              return;
            }
            this.featureValues[featureId] = newVal;
          },
          'clear': (feature) => {
            if (feature.id === 'price') {
              this.priceRange = emptyPriceValue.create();
              return;
            }
            this.featureValues[feature.id] = this.generateEmptyFeatureValue(feature);
          },
        }
      }
    },

    emptyFeatureValues() {
      return emptyFeatureValues;
    },

    filtersElId() {
      return Math.random();
    },
  },

  methods: {
    resetProductsQuery() {
      this.currentPage = 1
      this.generateFeatureValues()
      this.priceRange = emptyPriceValue.create();
    },

    loadNextPage() {
      if (this.productsAreLoading) {
        return
      }

      this.currentPage++
      this.loadProducts('append')
    },

    async fullReload(type = 'reset') {
      this.resetProductsQuery()
      return Promise.all([
        this.loadProducts(type),
        this.loadFeatures(),
        (async () => {
          this.availablePriceRange = null;

          if (cachedState.prerendering()) {
            return
          }

          const req = axios.get('/products/prices', { params: this.productsRequestQuery });
          this.availablePriceRange = (await req).data.data;
        })(),
      ]);
    },

    /**
     * @param {'reset' | 'append'} type
     */
    async loadProducts(type = 'reset') {
      if (process.env.NODE_ENV !== 'production') {
        if (!['reset', 'append'].includes(type)) {
          throw new Error('Unexpected operation type');
        }
      }

      this.lastLoadingType = type;

      if (type === 'reset') {
        this.currentPage = 1;
      }

      this.forcePreloader = false

      const req = axios.get('products', { params: this.productsRequestQuery })
      this.lastProductsReqest = req;

      let data;
      let thisStartedTheLast;
      try {
        ({ data } = (await req));
      } finally {
        thisStartedTheLast = req === this.lastProductsReqest;
        if (req === this.lastProductsReqest) {
          this.lastProductsReqest = null;
        }
      }
      if (!thisStartedTheLast) {
        return;
      }

      const newProducts = data.data;
      if (type === 'append') {
        this.products = this.products.concat(newProducts);
      } else {
        this.products = newProducts;
      }
      this.$set(this, 'pagination', data.meta.pagination)
      this.loadedProductsAtLeastOnce = true
    },

    async loadFeatures() {
      if (cachedState.prerendering()) {
        return
      }

      const category = this.category && this.category.id ? this.category.id : this.categoryId

      if (!category) {
        return;
      }

      this.watchersState.features = false

      const req = axios.get(`categories/${category}/features`);
      this.lastFeaturesRequest = req;

      let newFeatures;
      let thisStartedTheLast;
      try {
        newFeatures = (await req).data.data;
      } finally {
        thisStartedTheLast = req === this.lastFeaturesRequest;
        if (thisStartedTheLast) {
          this.lastFeaturesRequest = null;
        }
      }
      if (!thisStartedTheLast) {
        return;
      }
      this.features = newFeatures;
      this.generateFeatureValues()
      this.$nextTick(() => {
        this.watchersState.features = true
      })
    },

    sortFeatures(features) {
      return features.map(feature => {
        feature.values = feature.values.sort((first, second) => {
          const firstName = this.parseFeatureValue(first.name)
          const secondName = this.parseFeatureValue(second.name)

          if (firstName > secondName) {
            return 1
          } else if (firstName < secondName) {
            return -1
          }

          return 0
        })

        return feature
      })
    },

    parseFeatureValue(value) {
      if (parseFloat(value).toString() === value) {
        return parseFloat(value)
      }

      return value
    },

    generateEmptyFeatureValue(feature) {
      return (emptyFeatureValues[feature.field_type.id] || emptyFeatureValues.default).create();
    },

    isFeatureValueEmpty(feature, value) {
      return (emptyFeatureValues[feature.field_type.id] || emptyFeatureValues.default).isEmpty(value);
    },

    generateFeatureValues() {
      let values = {}

      if (this.hasFeatureFilters) {
        this.features.forEach(feature => values[feature.id] = this.generateEmptyFeatureValue(feature))
      }

      this.featureValues = values
    },
  },

  watch: {
    category(category) {
      if (!this.watchersState.category) {
        return
      }

      this.filtersAreActive = false
      this.watchersState.features = false
      this.fullReload()
        .then(() => {
          this.watchersState.features = true
        })
        .catch(err => {
          console.error(err)
          this.watchersState.features = true
        })
    },

    sorting() {
      if (!this.watchersState.sorting) {
        return
      }

      this.loadProducts('reset')
    },

    '$store.state.store.activeCityId'() {
      if (!this.watchersState.activeCityId) return;
      this.loadProducts('reset');
    },
  },

  data() {
    return {
      products: [],
      availablePriceRange: null,
      priceRange: emptyPriceValue.create(),
      features: [],
      featureValues: {},
      filtersAreActive: false,
      mobileExpandedControls: {
        sorting: true,
        // tags: false,
        // itemDisplay: false,
      },
      forcePreloader: true,
      lastProductsReqest: null,
      lastLoadingType: null,
      loadedProductsAtLeastOnce: false,
      lastFeaturesRequest: null,
      watchersState: {
        category: false,
        features: false,
        sorting: false,
        activeCityId: false,
      },
      currentPage: 1,
      pagination: {
        current_page: 1,
        total_pages: 1,
        total: 0,
      },
      sorting: 'manual',
      availableSorting: {
        manual: {
          label: 'По порядку',
          order_by: 'manual',
        },
        priceUp: {
          label: 'Сначала дешевле',
          order_by: 'price',
        },
        priceDown: {
          label: 'Сначала дороже',
          order_by: '-price',
        },
      },
    }
  },
}
</script>
